import {
    ChangeDetectionStrategy,
    Component, computed,
    contentChildren,
    effect,
    inject,
    InjectionToken,
    output,
    OutputEmitterRef, signal,
    Signal,
    viewChild, WritableSignal
} from "@angular/core";
import {AbstractControl, FormControl, FormsModule, NgForm, NgModel, ValidationErrors} from "@angular/forms";
import {ValidationMessage} from "@fedelta-pos/data-client";
import {MessageDetails, MessageLevel} from "../../notification/message/message";
import {NotificationService} from "../../notification/notification.service";

export const FORM_CONTROL: InjectionToken<FormComponent> = new InjectionToken("FORM_CONTROL");

export abstract class AbstractModelContainer {
    public abstract getModels(): ReadonlyArray<NgModel>;
}

@Component({
    selector: "woa-form",
    templateUrl: "./form.component.html",
    styleUrl: "./form.component.scss",
    imports: [
        FormsModule
    ],
    providers: [{provide: FORM_CONTROL, useExisting: FormComponent, multi: true}],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormComponent {
    private notificationService: NotificationService = inject(NotificationService);

    public onSubmit: OutputEmitterRef<any> = output<any>();

    private models: Signal<ReadonlyArray<NgModel>> = contentChildren(NgModel, {descendants: true});
    private modelContainers: Signal<ReadonlyArray<AbstractModelContainer>> = contentChildren(AbstractModelContainer, {descendants: true});
    private ngForm: Signal<NgForm> = viewChild.required<NgForm>("ngForm");

    private _submitted: WritableSignal<boolean> = signal<boolean>(false);
    public submitted: Signal<boolean> = this._submitted.asReadonly();

    public hasError: Signal<(field: FormControl | AbstractControl, type?: string) => boolean> = computed(() => {
        const submitted: boolean = this.submitted();
        return (field: FormControl | AbstractControl, type?: string) =>
            field && (submitted || field["_dirty"]) && field.errors && (!type || field.errors[type]);
    });

    constructor() {
        effect(() => {
            Object.keys(this.ngForm().controls).forEach((key: string) => this.ngForm().form.removeControl(key));
            this.models().forEach((model: NgModel) => this.ngForm().addControl(model));
            this.modelContainers().forEach((modelContainer: AbstractModelContainer) => {
                if (modelContainer.getModels()) {
                    modelContainer.getModels().forEach((model) => this.ngForm().addControl(model));
                }
            });
        });
    }

    public submit(event?: any): void {
        this._submitted.set(true);
        this.ngForm().onSubmit(event);
    }

    public get valid(): boolean {
        return this.ngForm().valid;
    }

    public markAsUnsubmitted(): void {
        this._submitted.set(false);
        this.ngForm["_submitted"] = false;
    }

    public reset(): void {
        this.markAsUnsubmitted();
        for (let controlName in this.ngForm().controls) {
            this.ngForm().controls[controlName]["_dirty"] = false;
        }
    }

    public applyServerValidationMessages(validationMessages: ValidationMessage[]) {
        validationMessages.forEach(validationMessage => {
            const control: AbstractControl<any, any> = validationMessage.fieldPath ? this.ngForm().controls[validationMessage.fieldPath] : null;
            if (!control) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, validationMessage.messageKey, validationMessage.messageParams));
                return;
            }

            const existingErrors: ValidationErrors = control.errors ?? {};
            control.setErrors({
                ...existingErrors,
                server: {
                    validationMessages: [...(existingErrors['server']?.validationMessages ?? []), validationMessage]
                }
            });
        });
    }
}
